import api from '@/libs/axiosInstance';
import type { ApiResponse, DisplayRightsCompositionContextResponse } from '@fluenceur/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

export const displayRightsCompositionQueryKey = (authorizationId: string | null | undefined) =>
  ['displayRightsComposition', authorizationId] as const;

const fetchDisplayRightsComposition = async (
  authorizationId: string
): Promise<DisplayRightsCompositionContextResponse> => {
  const response = await api.get<ApiResponse<DisplayRightsCompositionContextResponse>>(
    `/dashboard/display-rights/authorizations/${authorizationId}/composition`
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Display rights composition context is unavailable.');
  }

  return response.data.data;
};

/**
 * What the composer may offer for one authorization: the gesture (`action`), the
 * currency, the version in play, the renewal pre-fill and whether the connected
 * mailbox can carry the link.
 *
 * Always refetched when the composer opens: the gesture depends on state the
 * creator can change from their own page (accept, decline), so a cached answer
 * would let the brand compose against a negotiation that has already moved.
 */
export const useDisplayRightsCompositionQuery = (
  authorizationId: string | null | undefined,
  enabled = true
): UseQueryResult<DisplayRightsCompositionContextResponse, Error> =>
  useQuery<DisplayRightsCompositionContextResponse, Error>({
    queryKey: displayRightsCompositionQueryKey(authorizationId),
    queryFn: () => fetchDisplayRightsComposition(authorizationId!),
    enabled: !!authorizationId && enabled,
    refetchOnMount: 'always',
    staleTime: 0,
    retry: 1,
  });
